import React, { useContext, useEffect, useMemo } from 'react'

import {
  BackdropLoader,
  Box,
  Card,
  DateRangePresetOption,
  LinearProgress,
  SimpleTableProps,
  Typography,
  makeAppStyles,
  Grid,
  ProviderLogo,
  formatMoney,
  ReportRange,
  MenuOption,
  Divider,
  RoundedPlainTextButton,
  CircularProgress,
  Dialog,
  Loader,
  BoxProps,
  useAppTheme,
  lighten,
  GridSize,
  Tabs,
  Tab,
  InlineFilterPanel,
  InlineFilterPanelConfigItem,
  MenuItem,
  darken,
  SimpleTable,
} from '@percept/mui'

import { CloudDownload, ErrorOutline, Info } from '@percept/mui/icons'

import {
  stringifyUrlSearchParams,
  useDocumentTitle,
  useUrlState,
} from '@percept/hooks'

import {
  DataWarehouseProvider,
  DataWarehouseQueryHookProps,
  DataWarehouseReportGrouping,
  useDataWarehouseExport,
  useDataWarehouseQuery,
} from '../hooks'

import { currencyOptions } from 'components/CurrencySelect'

import {
  GenericTableRow,
  getDataWarehouseTableProps,
  getMetricLabel,
  isNewLayout,
  monetaryColumns,
  rateBasedColumns,
} from '../lib'

import { get, intersection, uniq } from 'lodash-es'

import { deslugify, deslugifyWithSeparator, displayNumber, isPlatformUnitContainer, produceKeyedMapping } from '@percept/utils'

import { useSavedQueryStrings, useTargetCurrency } from 'hooks'

import {
  dateRangeValueToDateRangeUrlState,
  getAvailableDateRange,
  getAvailableReportGroupings,
  getLatestProviderOverlap,
  getOrgProviderAvailability,
  getOrgProviderAvailabilityByGrouping,
  getSupportedProviders,
  isProviderCompatible,
  resolveDateRangeValue,
  resolveProviderAvailability,
} from '../utils'

import { providerLabelMap } from '@percept/constants'

import { TimeseriesGranularity } from '@percept/types'

import { ChartBuilderDimensionType, ChartBuilderFilterPanelState, ChartBuilderLayoutType, ChartBuilderState } from './typings'
import { getBaseSegmentations, getGranularityOptions, getNearestNeighbourGranularity, MetricLabelStrategy, warehouseResponseToChartData } from './dataUtils'
import { ExportableMultiChart, MultiChartData } from '@percept/mui/charts'
import {
  calendarYearFormatter,
  dmyFormatter,
  financialYearFormatter,
  getMoneyFormatter,
  quarterFormatter,
  rateFormatter,
  shortMonthYearFormatter,
  SVGDatumType,
  weekFormatter,
} from '@percept/mui/charts'
import { TickFormatter } from '@visx/axis'
import { OrgUnitContext, TimelineDatum } from '@percept/app-components'
import { OrgUnitDataWarehouseAvailabilityContext } from 'contexts'
import { getMarkerUrl } from '@percept/mui/charts'


const granularityValues: TimeseriesGranularity[] = [
  'DAY',
  'WEEK',
  'MONTH',
  'QUARTER',
  'CALENDAR_YEAR',
  'FINANCIAL_YEAR',
]

const granularityOptions: MenuOption<TimeseriesGranularity>[] = granularityValues.map( value => ({
  value,
  label: deslugify(value),
}))


const useStyles = makeAppStyles( theme => ({
  filterCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    minHeight: 'min-content',
  },
  dialogPaper: {
    alignSelf: 'flex-start',
  },
  radioFormLabelTopAligned: {
    alignItems: 'start',
  },
  filterSelector: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: theme.spacing(2),
    '&:last-of-type': {
      marginRight: 0,
    },
  },
  filterLabel: {
    fontSize: 11,
    marginLeft: 10,
    marginBottom: 6,
  },
  reportTitle: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(1.5),
  },
  reportDateRange: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1.5),
    color: theme.palette.text.secondary,
  },
  groupingTitle: {
    fontSize: 14,
    marginTop: theme.spacing(1),
  },
  groupingExplainer: {
    marginTop: 2,
    fontSize: 12,
    lineHeight: 1.5,
    maxWidth: '14rem',
    whiteSpace: 'normal',
    marginBottom: theme.spacing(1.5),
  },
  reportTypeFormLabel: {
    minWidth: 'max-content',
    marginRight: theme.spacing(1),
  },
  reportTypeButton: {
    minWidth: 'max-content',
  },
  reportTypeInfo: {
    marginTop: theme.spacing(0.75),
    marginLeft: theme.spacing(3),
    flexShrink: 1,
    fontSize: 16,
    color: theme.palette.text.secondary,
  },
  reportTypeLabelTitle: {
    fontSize: 16,
    fontWeight: theme.typography.fontWeightBold,
  },
  reportTypeProviderChip: {
    marginLeft: theme.spacing(1),
    '&:first-of-type': {
      marginLeft: theme.spacing(2),
    },
  },
  reportTypeProviderChipRoot: {
    height: 20,
  },
  reportTypeProviderChipLabel: {
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
  },
  reportTypeLabelInfo: {
    fontSize: 12,
    maxWidth: '35rem',
    whiteSpace: 'normal',
    lineHeight: 1.4,
    paddingTop: 2,
    paddingBottom: 6,
  },
  formGroup: {
    backgroundColor: theme.palette.background.paper,
  },
  formControlLabelTopAligned: {
    alignItems: 'start',
  },
  formControlLabelPrimaryText: {
    fontSize: 14,
    marginTop: theme.spacing(1),
  },
  formControlLabelSecondaryText: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 2,
    fontSize: 13,
    lineHeight: 1.5,
    maxWidth: '21rem',
    whiteSpace: 'normal',
    marginBottom: theme.spacing(1.5),
  },
  tableCard: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  chartCard: {
    padding: theme.spacing(4, 2, 2, 2),
    marginBottom: theme.spacing(2),
    position: 'relative',
  },
  tabs: {
    // marginBottom: theme.spacing(5),
    // marginTop: theme.spacing(-4),
    margin: theme.spacing(-3, 2, 5, 2),
  },
  tab: {
    textTransform: 'none',
  },
  tablePaginationRoot: {
    overflow: 'unset',
  },
  refetchIndicator: {
    height: 4,
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: theme.zIndex.appBar + 4,
  },
  loaderContent: {
    marginTop: theme.spacing(1.5),
  },
}))


const urlStateToChartBuilderFilterPanelState = ({
  state,
  minDate,
  maxDate,
}: {
  state: ChartBuilderState,
  minDate: Date | null,
  maxDate: Date | null,
}): ChartBuilderFilterPanelState => ({
  ...state,
  dateRange: resolveDateRangeValue({
    dateRangePreset: state.dateRangePreset,
    start: state.start,
    end: state.end,
    minDate,
    maxDate,
  })
})

const getSegmentationOptions = (
  groupingsByKey: Record<string, DataWarehouseReportGrouping> | undefined | null
): MenuOption<ChartBuilderDimensionType | null>[] => {
  if( !groupingsByKey ){
    return []
  }
  const defaultOptions: MenuOption<ChartBuilderDimensionType | null>[] = [
    { value: null, label: 'None' },
    { value: 'organisation', label: 'Organisation' },
    { value: 'provider', label: 'Provider' },
  ]
  if( groupingsByKey.CAMPAIGN_OBJECTIVE ){
    defaultOptions.push({ value: 'campaign-objective', label: 'Objective' })
  }
  if( groupingsByKey.MARKETING_FUNNEL ){
    defaultOptions.push({ value: 'marketing-funnel', label: 'Marketing Funnel' })
  }
  return defaultOptions
}

const getReportType = (dimensionType: ChartBuilderDimensionType | null): string => {
  if( dimensionType === 'campaign-objective' ) return 'CAMPAIGN_OBJECTIVE'
  if( dimensionType === 'marketing-funnel' ) return 'MARKETING_FUNNEL'
  return 'UNSEGMENTED'
}


const dateRangePresetOptions: DateRangePresetOption[] = (
  process.env.WORKLOAD_CONTEXT === 'VODAFONE' ? [
    'current-financial-year',
    'current-year',
    'last-financial-year',
    'last-year',
    'last-quarter',
    'last-month',
    'last-week',
    'last-30-days',
    'last-7-days',
    'custom',
  ] : [
    'current-year',
    'last-year',
    'last-6-months',
    'last-3-months',
    'last-quarter',
    'last-month',
    'last-week',
    'last-30-days',
    'last-7-days',
    'custom',
  ]
)


const metricOptionKeys = [
  'cost',
  'impressions',
  'clicks',
  'conversions',
  'video_views',
  'cost_per_conversion',
  'average_cpc',
  'average_cpm',
  'average_ctr',
  'average_cpv',
]


// Consistent loader key to avoid re-render affecting animation
const LOADER_KEY = 'dwh-loader'

const darkenColor = (color: string): string => (
  darken(color, 0.2)
)

const lightenColor = (color: string): string => (
  lighten(color, 0.3)
)

const dropShadowFilter = (
  <filter id='dropShadow' x='-50%' y='-50%' width='200%' height='200%'>
    <feGaussianBlur in='SourceAlpha' stdDeviation='3'/>
    <feOffset dx='2' dy='2' result='offsetblur'/>
    <feComponentTransfer>
      <feFuncA type='linear' slope='0.5'/>
    </feComponentTransfer>
    <feMerge>
      <feMergeNode/>
      <feMergeNode in='SourceGraphic'/>
    </feMerge>
  </filter>
)

const getFormatter = (state: ChartBuilderState, metricKey: 'barChart' | 'lineChart'): TickFormatter<any> | undefined => {
  const metric = state[metricKey]
  if( !metric ){
    return undefined
  }
  if( monetaryColumns.includes(metric) ){
    return getMoneyFormatter(state.currency)
  }
  if( rateBasedColumns.includes(metric) ){
    return rateFormatter
  }
  return displayNumber
}

const getTimelineFormatter = (granularity: TimeseriesGranularity): TickFormatter<any> => {
  switch(granularity){
    case 'CALENDAR_YEAR': return calendarYearFormatter
    case 'FINANCIAL_YEAR': return financialYearFormatter
    case 'QUARTER': return quarterFormatter
    case 'MONTH': return shortMonthYearFormatter
    case 'WEEK': return weekFormatter
    default: return dmyFormatter
  }
}

type ChartConfig<T extends SVGDatumType = TimelineDatum> = {
  key: string
  title: string
  barChartDatasets?: MultiChartData<T>[] | null
  lineChartDatasets?: MultiChartData<T>[] | null
}

export const ChartBuilder = () => {

  const orgUnit = useContext(OrgUnitContext)

  useDocumentTitle({
    paths: [
      orgUnit.name,
      'Chart Builder',
    ]
  })

  const orgUnitId = orgUnit.id

  const isContainerOrg = isPlatformUnitContainer(orgUnit)

  const isSingleChildContainerOrg = isContainerOrg && orgUnit.children.length === 1

  const { reportGroupings: groupings, availability } = useContext(OrgUnitDataWarehouseAvailabilityContext)

  const orgAvailabilityByGrouping = useMemo(() => {
    if( availability && orgUnit ){
      return getOrgProviderAvailabilityByGrouping(
        availability,
        orgUnit
      )
    }
    return null
  }, [availability, orgUnit])

  const reportGroupings = useMemo(() => {
    return getAvailableReportGroupings({
      availability: availability,
      reportGroupings: groupings,
    })
  }, [availability, groupings])

  const defaultOrganisations = isContainerOrg ? orgUnit.children.map( c => c.id) : [orgUnitId]

  const [targetCurrency] = useTargetCurrency()

  const [state, setState] = useUrlState<ChartBuilderState>({
    dateRangePreset: 'last-30-days',
    start: undefined,
    end: undefined,
    granularity: 'DAY',
    organisations: defaultOrganisations,
    providers: [],
    dimensionType: null,
    dimensions: [],
    layout: 'single-chart',
    barChart: 'cost',
    lineChart: 'impressions',
    currency: targetCurrency,
    tab: undefined,
  })

  const reportType = getReportType(state.dimensionType)

  const [, setSavedQueryStrings] = useSavedQueryStrings()

  useEffect(() => {
    if( orgUnitId ){
      setSavedQueryStrings({
        [`chartBuilder|${orgUnitId}`]: stringifyUrlSearchParams(state)
      })
    }
  }, [state, orgUnitId, setSavedQueryStrings])

  const groupingsByKey = produceKeyedMapping(reportGroupings, 'key')

  const segmentationOptions = getSegmentationOptions(groupingsByKey)

  const grouping = get(groupingsByKey, reportType || '', null)

  const orgProviderAvailability = getOrgProviderAvailability({
    orgAvailabilityByGrouping,
    reportGrouping: grouping,
  })

  const [minDate, maxDate] = getAvailableDateRange({
    providerAvailability: resolveProviderAvailability({
      orgUnitIds: state.organisations,
      orgProviderAvailability,
    }),
    providers: state.providers,
  })

  const dateRangeValue = resolveDateRangeValue({
    dateRangePreset: state.dateRangePreset,
    start: state.start,
    end: state.end,
    minDate,
    maxDate,
  })

  useEffect(() => {
    if(
      !state.providers.length
      && !state.organisations.includes('NONE')
      && reportGroupings.length
      && orgAvailabilityByGrouping
    ){
      const orgProviderAvailability = getOrgProviderAvailability({
        orgAvailabilityByGrouping,
        reportGrouping: grouping,
      })

      const providerAvailability = resolveProviderAvailability({
        orgUnitIds: state.organisations,
        orgProviderAvailability,
      })

      const overlappingProviders = getLatestProviderOverlap(providerAvailability)
      const availableProviders = overlappingProviders

      const matchedProvidersToState = intersection(state.providers, availableProviders)

      const providers = (
        matchedProvidersToState.length ?
          matchedProvidersToState :
          availableProviders
      )

      const [minDate, maxDate] = getAvailableDateRange({
        providerAvailability: resolveProviderAvailability({
          orgUnitIds: state.organisations,
          orgProviderAvailability,
        }),
        providers,
      })

      const dateRangeValue = resolveDateRangeValue({
        dateRangePreset: state.dateRangePreset,
        minDate,
        maxDate,
        start: state.start,
        end: state.end,
      })

      const update: Partial<ChartBuilderState> = {
        providers,
        ...dateRangeValueToDateRangeUrlState(dateRangeValue),
      }
      setState(update)
    }
  }, [
    state.providers,
    state.dateRangePreset,
    state.start,
    state.end,
    state.organisations,
    reportGroupings,
    grouping,
    orgAvailabilityByGrouping,
    setState
  ])

  const queryHookProps: DataWarehouseQueryHookProps = {
    grouping: grouping && grouping.key,
    org_unit_ids: state.organisations,
    providers: state.providers,
    start: dateRangeValue.value[0] && new Date(dateRangeValue.value[0]) || null,
    end: dateRangeValue.value[1] && new Date(dateRangeValue.value[1]) || null,
    timeseries_granularity: state.granularity,
    currency: state.currency,
    base_segmentations: getBaseSegmentations(state.dimensionType),
    aggregate_to_single_org_unit: !!(
      orgUnit
      && state.organisations.length === 1
      && orgUnit.children
      && orgUnit.children.length
      && state.organisations[0] === orgUnit.id
    ),
    enabled: !state.organisations.includes('NONE'),
  }

  const queryResult = useDataWarehouseQuery(queryHookProps)

  const totalsQueryResult = useDataWarehouseQuery({
    ...queryHookProps,
    timeseries_granularity: undefined,
  })

  const exportQueryResult = useDataWarehouseExport()

  const appTheme = useAppTheme()

  const { barChartDatasets, lineChartDatasets } = useMemo(() => {
    if( !queryResult.data ){
      return { barChartDatasets: null, lineChartDatasets: null }
    }
    const isCombinedChart = !!(state.barChart && state.lineChart)
    const isSingleChartView = !state.layout || state.layout === 'single-chart'
    let metricLabelStrategy: MetricLabelStrategy = null
    if( isCombinedChart ){
      metricLabelStrategy = isSingleChartView ? 'merge' : 'override'
    }
    return {
      barChartDatasets: !state.barChart ? null : warehouseResponseToChartData({
        queryResponse: queryResult.data,
        state,
        metric: state.barChart,
        metricLabelStrategy,
        appTheme,
        colorTransform: (
          state.lineChart ? darkenColor : undefined
        ),
      }),
      lineChartDatasets: !state.lineChart ? null : warehouseResponseToChartData({
        queryResponse: queryResult.data,
        state,
        metric: state.lineChart,
        metricLabelStrategy,
        appTheme,
        colorTransform: (
          state.barChart ? lightenColor : undefined
        ),
        getSVGPathProps: (key) => {
          const markerUrl = `url(#${getMarkerUrl(key)})`
          return {
            markerStart: markerUrl,
            markerMid: markerUrl,
            markerEnd: markerUrl,
            filter: state.barChart ? 'url(#dropShadow)' : undefined,
            strokeWidth: 3,
          }
        },
      })
    }
  }, [queryResult.data, state, appTheme])

  const tableProps: SimpleTableProps<GenericTableRow> = useMemo(() => {
    if( !queryResult.data ){
      return {
        grouped: false,
        columns: [],
        rows: [],
      } as unknown as SimpleTableProps<GenericTableRow>
    }
    return getDataWarehouseTableProps(
      queryResult.data,
      state.currency,
      state.granularity,
      totalsQueryResult.data,
    ) as SimpleTableProps<GenericTableRow>
  }, [queryResult.data, totalsQueryResult.data, state.currency, state.granularity])

  const classes = useStyles()

  if( !availability || !groupings ){
    return (
      <BackdropLoader key={LOADER_KEY}>
        <Typography variant='h3' className={classes.loaderContent}>
          Loading report availability...
        </Typography>
      </BackdropLoader>
    )
  }

  const orgUnitType = deslugify(orgUnit && orgUnit.unit_type || 'ORGANISATION')

  const boxProps: BoxProps = (
    isNewLayout ?
      { pt: 2 } :
      { pt: 5 }
  )

  let chartConfigs: ChartConfig[] = []
  const gridSizes: Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', GridSize> = {
    xs: 12,
    sm: 12,
    md: 12,
    lg: 12,
    xl: 12,
  }
  const { layout = 'single-chart' } = state
  let activeTab = state.tab
  if( layout === 'single-chart' ){
    chartConfigs = [
      {
        key: 'single-chart',
        title: '',
        barChartDatasets,
        lineChartDatasets,
      }
    ]
  }else{
    const barDatasetsByLabel = produceKeyedMapping(barChartDatasets, 'key')
    const lineDatasetsByLabel = produceKeyedMapping(lineChartDatasets, 'key')
    const keyOrder = uniq([
      ...(barChartDatasets || []).map( d => d.key ),
      ...(lineChartDatasets || []).map( d => d.key ),
    ])
    if( layout === 'tabs' && (!activeTab || !keyOrder.includes(activeTab)) ){
      activeTab = keyOrder[0]
    }
    chartConfigs = keyOrder.map( key => {
      const matchedBarDataset = barDatasetsByLabel[key]
      const matchedLineDataset = lineDatasetsByLabel[key]
      return {
        key,
        title: get(matchedBarDataset, 'title') || get(matchedLineDataset, 'title'),
        barChartDatasets: matchedBarDataset && [matchedBarDataset],
        lineChartDatasets: matchedLineDataset && [matchedLineDataset],
      }
    })
    const numCharts = chartConfigs.length
    if( numCharts > 1 && layout === 'grid' ){
      gridSizes.md = 6
      gridSizes.lg = numCharts > 3 ? 4 : 6
      gridSizes.xl = numCharts > 4 ? 2 : numCharts > 3 ? 3 : numCharts > 2 ? 4 : 6
    }
  }

  const yTickFormatterBarChart = getFormatter(state, 'barChart')
  const yTickFormatterLineChart = getFormatter(state, 'lineChart')
  const xTickFormatter = getTimelineFormatter(state.granularity)

  const markerSize = 4

  const values = urlStateToChartBuilderFilterPanelState({ state, minDate, maxDate })

  const updateValues = (update: Partial<ChartBuilderFilterPanelState>): void => {
    setState({
      ...values,
      ...update,
      ...dateRangeValueToDateRangeUrlState(update.dateRange || values.dateRange),
    })
  }

  const activeGrouping = (reportType && groupingsByKey[reportType]) || null

  const providerAvailability = resolveProviderAvailability({
    orgUnitIds: values.organisations,
    orgProviderAvailability,
  })

  const supportedProviders = getSupportedProviders({
    rootOrgUnitId: orgUnitId,
    reportGrouping: activeGrouping,
    orgProviderAvailability,
  })

  const providerOptions: MenuOption<DataWarehouseProvider>[] = (
    supportedProviders.map( provider => {
      // Some provider date ranges may be completely incompatible with the selected
      // date range or other providers. If this is the case we need to disable the
      // option.
      const matchedAvailability = providerAvailability.find( a => a.provider === provider )
      const isCompatible = !!matchedAvailability && isProviderCompatible({
        provider,
        selectedProviders: values.providers,
        providerAvailability,
      })
      return {
        value: provider,
        disabled: !isCompatible,
        label: (
          <div>
            <ProviderLogo
              className={classes.formControlLabelPrimaryText}
              size={1.35} provider={provider} style={{position: 'relative', top: 3}} />
            { matchedAvailability ? (
              <ReportRange
                TypographyProps={{className: classes.formControlLabelSecondaryText}}
                start={matchedAvailability.start}
                end={matchedAvailability.end} />
            ) : (
              <Typography
                className={classes.formControlLabelSecondaryText}>
                { values.organisations.length ? 'No data available' : `No ${orgUnitType.toLowerCase()}s selected` }
              </Typography>
            )}
          </div>
        ),
      }
    })
  )

  const orgIdOptions = (orgUnit.children || [orgUnit]).map( c => c.id ).filter( id => !!orgProviderAvailability[id] )

  const updateOrganisations = (organisations: string[]): void => {
    if( !organisations.length ){
      updateValues({ organisations: ['NONE'] })
      return
    }
    organisations = organisations.filter( orgUnitId => (
      !!orgProviderAvailability[orgUnitId]
    ))
    const providerAvailability = resolveProviderAvailability({
      orgUnitIds: organisations,
      orgProviderAvailability,
    })
    const providerOptions = getLatestProviderOverlap(providerAvailability)
    const commonProviders = intersection(
      providerOptions,
      values.providers,
    )
    const providers = commonProviders.length ? commonProviders : providerOptions
    const [minDate, maxDate] = getAvailableDateRange({
      providerAvailability,
      providers,
    })
    updateValues({
      providers,
      organisations,
      dateRange: resolveDateRangeValue({
        dateRangePreset: values.dateRange.preset,
        start: values.dateRange.value[0],
        end: values.dateRange.value[1],
        minDate,
        maxDate,
      })
    })
  }

  const supportedGranularities = getGranularityOptions(values.dateRange.value)

  return (
    <Box
      display='flex'
      flexDirection='column'
      px={3}
      pb={1}
      {...boxProps}>

      <Card className={classes.filterCard}>
        <InlineFilterPanel
          values={values}
          color='secondary'
          config={[
            {
              type: 'GridItem',
              key: 'periodFilters',
              xs: 12,
              children: [
                {
                  type: 'DateRange',
                  name: 'Date Range',
                  key: 'dateRange',
                  value: values.dateRange.value as [Date, Date],
                  dateRangePreset: values.dateRange.preset,
                  dateRangePresetOptions,
                  minDate,
                  maxDate,
                  onChange: (value, preset): void => {
                    const update: Partial<ChartBuilderFilterPanelState> = {
                      dateRange: { value, preset }
                    }
                    const supportedGranularities = getGranularityOptions(value)
                    if( !supportedGranularities.includes(values.granularity) ){
                      update.granularity = getNearestNeighbourGranularity(values.granularity, supportedGranularities)
                    }
                    updateValues(update)
                  },
                },
                {
                  type: 'SingleOptionGroup',
                  name: 'Granularity',
                  value: state.granularity,
                  options: granularityOptions.map( g => ({
                    ...g,
                    disabled: !supportedGranularities.includes(g.value),
                  })),
                  key: 'granularity',
                  renderLabel: (value) => value && deslugify(value as string),
                  onChange: (granularity: TimeseriesGranularity): void => {
                    updateValues({ granularity })
                  },
                },
                {
                  type: 'SingleOptionGroup',
                  name: 'Segment By',
                  value: state.dimensionType,
                  options: segmentationOptions,
                  key: 'dimensionType',
                  renderLabel: (value) => value ? deslugifyWithSeparator(value as string, '-') : 'None',
                  onChange: (dimensionType: ChartBuilderDimensionType | null): void => {
                    const previousReportType = getReportType(state.dimensionType)
                    const reportType = getReportType(dimensionType)
                    if( previousReportType === reportType ){
                      const update: Partial<ChartBuilderFilterPanelState> = { dimensionType }
                      if( !dimensionType && values.layout !== 'single-chart' ){
                        update.layout = 'single-chart'
                        update.tab = null
                      }
                      updateValues(update)
                      return
                    }

                    const activeGrouping = (reportType && groupingsByKey[reportType]) || null
                    const orgProviderAvailability = getOrgProviderAvailability({
                      orgAvailabilityByGrouping,
                      reportGrouping: activeGrouping,
                    })
                    const providerAvailability = resolveProviderAvailability({
                      orgUnitIds: values.organisations,
                      orgProviderAvailability,
                    })
                    const [minDate, maxDate] = getAvailableDateRange({
                      providerAvailability,
                      providers: values.providers,
                    })
                    // const supportedProviders = getSupportedProviders({
                    //   rootOrgUnitId: orgUnitId,
                    //   reportGrouping: activeGrouping,
                    //   orgProviderAvailability,
                    // })
                    const providerOptions = getLatestProviderOverlap(providerAvailability)
                    const commonProviders = intersection(
                      providerOptions,
                      values.providers,
                    )
                    const providers = commonProviders.length ? commonProviders : providerOptions

                    const update: Partial<ChartBuilderFilterPanelState> = {
                      dimensionType,
                      providers,
                      dateRange: resolveDateRangeValue({
                        dateRangePreset: values.dateRange.preset,
                        start: values.dateRange.value[0],
                        end: values.dateRange.value[1],
                        minDate,
                        maxDate,
                      })
                    }
                    if( !dimensionType && values.layout !== 'single-chart' ){
                      update.layout = 'single-chart'
                    }
                    const supportedGranularities = getGranularityOptions(
                      update.dateRange && update.dateRange.value || values.dateRange.value
                    )
                    if( !supportedGranularities.includes(values.granularity) ){
                      update.granularity = getNearestNeighbourGranularity(values.granularity, supportedGranularities)
                    }

                    updateValues(update)
                  },
                },
                {
                  type: 'MultiOptionGroup',
                  name: 'Platforms',
                  key: 'providers',
                  value: state.providers,
                  options: providerOptions,
                  renderLabel: (value) => providerLabelMap[value as DataWarehouseProvider],
                  onChange: (providers: DataWarehouseProvider[]): void => {
                    updateValues({ providers })
                  },
                },
                {
                  type: 'SingleOptionGroup',
                  name: 'Currency',
                  options: currencyOptions,
                  value: state.currency,
                  key: 'currency',
                  renderLabel: (value) => (
                    `${formatMoney({ amount: null, currency: value as string })} ${value}`
                  ),
                  onChange: (currency: string): void => {
                    updateValues({ currency })
                  },
                },
              ],
            },
            ...(isContainerOrg && !isSingleChildContainerOrg && [
              {
                type: 'GridItem',
                key: 'orgFilters',
                xs: 12,
                children: [
                  {
                    type: 'MultiOptionGroup',
                    name: 'Organisations',
                    options: orgUnit.children.map( c => ({
                      value: c.id,
                      label: c.name,
                      disabled: !orgProviderAvailability[c.id],
                    })),
                    contextMenuContent: orgIdOptions.length > 2 ? ({ onClose }) => (
                  <>
                    <MenuItem
                      disabled={
                        orgIdOptions.length === values.organisations.length
                      }
                      onClick={(): void => {
                        updateOrganisations(orgIdOptions)
                        onClose()
                      }}>
                      Select All
                    </MenuItem>
                    <MenuItem
                      disabled={!orgIdOptions.length}
                      onClick={(): void => {
                        updateValues({ organisations: ['NONE'] })
                        onClose()
                      }}>
                      Deselect All
                    </MenuItem>
                  </>
                    ) : null,
                    value: state.organisations,
                    key: 'organisations',
                    onChange: updateOrganisations,
                  },
                ],
              },
            ] as InlineFilterPanelConfigItem<ChartBuilderFilterPanelState>[] || []),
            {
              type: 'SingleOptionGroup',
              name: 'View As',
              value: state.layout,
              options: [
                { value: 'single-chart', label: 'Single Chart' },
                { value: 'tabs', label: 'Tabs', disabled: !state.dimensionType },
                { value: 'grid', label: 'Grid', disabled: !state.dimensionType },
              ] as MenuOption<ChartBuilderLayoutType | null>[],
              key: 'layout',
              renderLabel: (value) => value ? deslugifyWithSeparator(value as string, '-') : 'None',
              onChange: (layout: ChartBuilderLayoutType): void => {
                updateValues({ layout })
              },
            },
            {
              type: 'SingleOptionGroup',
              name: 'Bar Chart',
              value: state.barChart,
              options: [
                { value: null, label: 'None' },
                ...metricOptionKeys.map( value => ({
                  value,
                  label: getMetricLabel(value),
                  disabled: values.lineChart === value,
                }))
              ],
              key: 'barChart',
              renderLabel: value => value ? getMetricLabel(value) : 'None',
              onChange: (barChart: string): void => {
                updateValues({ barChart })
              },
            },
            {
              type: 'SingleOptionGroup',
              name: 'Line Chart',
              value: state.lineChart,
              options: [
                { value: null, label: 'None' },
                ...metricOptionKeys.map( value => ({
                  value,
                  label: getMetricLabel(value),
                  disabled: values.barChart === value,
                }))
              ],
              key: 'lineChart',
              renderLabel: value => value ? getMetricLabel(value) : 'None',
              onChange: (lineChart: string): void => {
                updateValues({ lineChart })
              },
            },
          ]} />
      </Card>

      { queryResult.isLoading && (
        <BackdropLoader key={LOADER_KEY}>
          <Typography variant='h3' className={classes.loaderContent}>
            Loading chart data...
          </Typography>
        </BackdropLoader>
      )}
      { exportQueryResult.isLoading && (
        <Dialog open maxWidth='sm'>
          <Box m={3}>
            <Loader justifySelf='center' alignSelf='center' mb={2} />
            <Typography variant='h4' className={classes.loaderContent}>
              Downloading chart data...
            </Typography>
          </Box>
        </Dialog>
      )}
      { queryResult.isError && (
        <Box p={6} display='flex' flexDirection='column' alignItems='center' justifyContent='center' color='text.secondary'>
          <Box display='flex' alignItems='center'>
            <Box display='flex' mr={1}>
              <ErrorOutline color='error' fontSize='large' />
            </Box>
            <Typography variant='h4' color='textSecondary'>
              Chart data could not be loaded
            </Typography>
          </Box>
          { queryResult.error.response && queryResult.error.response.data && queryResult.error.response.data.detail && (
            <Box mt={1}>
              <Typography variant='h5' color='textSecondary'>
                { queryResult.error.response.data.detail }
              </Typography>
            </Box>
          )}
        </Box>
      )}

      { state.organisations.includes('NONE') && (
        <Box p={6} display='flex' flexDirection='column' alignItems='center' justifyContent='center' color='text.secondary'>
          <Box display='flex' alignItems='center'>
            <Box display='flex' mr={1}>
              <Info color='inherit' fontSize='large' />
            </Box>
            <Typography variant='h5' color='textSecondary'>
              Please select one or more organisations
            </Typography>
          </Box>
        </Box>
      )}
      { queryResult.data && (
        <Card className={classes.chartCard}>
          { (queryResult.isRefetching || totalsQueryResult.isRefetching || totalsQueryResult.isLoading) && (
            <LinearProgress variant='indeterminate' className={classes.refetchIndicator} />
          )}
          { layout === 'tabs' && (
            <Tabs
              className={classes.tabs}
              variant='scrollable'
              scrollButtons='auto'
              value={activeTab}
              onChange={(e, tab): void => {
                setState({ tab })
              }}>
              { chartConfigs.map(({ key, title }) => (
                <Tab key={key} className={classes.tab} value={key} label={title} />
              )) }
            </Tabs>
          )}
          <Grid container spacing={5}>
            { chartConfigs.map(({ key, title, barChartDatasets, lineChartDatasets }) => {
              if( layout === 'tabs' && activeTab !== key ){
                return null
              }
              const displayDateRange = [
                dmyFormatter(queryResult.data.metadata.period.start_date),
                dmyFormatter(queryResult.data.metadata.period.end_date),
              ].join(' - ')
              let chartTitle = ''
              switch(layout){
                case 'tabs':
                  chartTitle = displayDateRange
                  break
                default:
                  chartTitle = `${title} ${displayDateRange}`
              }
              return (
                <Grid key={key} item {...gridSizes}>
                  <ExportableMultiChart
                    height={400}
                    title={chartTitle}
                    defs={
                      <>
                      {dropShadowFilter}
                      { (lineChartDatasets || []).map( pathDataset => {
                        const markerUrl = getMarkerUrl(pathDataset.key)
                        return (
                          <marker
                            key={markerUrl}
                            id={markerUrl}
                            viewBox={`0 0 ${markerSize * 2} ${markerSize * 2}`}
                            refX={markerSize}
                            refY={markerSize}
                            markerWidth={markerSize}
                            markerHeight={markerSize}>
                            <circle
                              cx={markerSize}
                              cy={markerSize}
                              r={markerSize}
                              fill={pathDataset.color} />
                          </marker>
                        )
                      })}
                      </>
                    }
                    barChartDatasets={barChartDatasets}
                    lineChartDatasets={lineChartDatasets}
                    xTickFormatter={xTickFormatter}
                    yTickFormatterBarChart={yTickFormatterBarChart}
                    yTickFormatterLineChart={yTickFormatterLineChart}
                    barChartAxisLabel={state.barChart && getMetricLabel(state.barChart)}
                    lineChartAxisLabel={state.lineChart && getMetricLabel(state.lineChart)}
                    LineComponentProps={{
                      svgPathProps: {
                        strokeWidth: 2,
                        filter: 'url(#dropShadow)',
                      },
                    }} />
                </Grid>
              )
            })}
          </Grid>
        </Card>
      )}
      { queryResult.data && (
        queryResult.data.rows.length === 0 ? (
          <Box p={6} display='flex' alignItems='center' justifyContent='center'>
            <Typography variant='h4' color='textSecondary'>
              No data is available for the { grouping ? `${grouping.title} report` : 'report' } with
              the selected filters applied
            </Typography>
          </Box>
        ) : (
          <Card className={classes.tableCard}>
            { queryResult.isRefetching && (
              <LinearProgress variant='indeterminate' className={classes.refetchIndicator} />
            )}
            { grouping && (
              <>
                <Box display='flex' justifyContent='flex-end'>
                  {/* <Typography variant='h5' className={classes.reportTitle}>
                    { grouping.title } Report
                    <ReportRange
                      TypographyProps={{
                        variant: 'inherit',
                        className: classes.reportDateRange,
                      }}
                      start={queryResult.data.metadata.period.start_date}
                      end={queryResult.data.metadata.period.end_date} />
                  </Typography> */}

                  <Box p={1.5}>
                    <RoundedPlainTextButton
                      variant='contained'
                      size='small'
                      startIcon={
                        exportQueryResult.isLoading ?
                          <CircularProgress size='1em' color='inherit' /> :
                          <CloudDownload />
                      }
                      disabled={exportQueryResult.isLoading}
                      onClick={(): void => {
                        exportQueryResult.mutate(queryHookProps)
                      }}>
                      Download
                    </RoundedPlainTextButton>
                  </Box>
                </Box>
                <Divider />
              </>
            )}
            <SimpleTable
              size='small'
              sortable
              stickyHeader
              unsetStickyHeaderZIndex={false}
              pinFirstColumn
              wrapCellText={false}
              {...tableProps} />
          </Card>
        )
      )}
    </Box>
  )
}
